import React, { useEffect, useState } from 'react';
import offerService from '../services/offerService';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../shared/providers/LanguageProvider';
import { ShimmerTable, ShimmerBox } from '../components/common/Shimmer';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const Offers = () => {
  const { token } = useAuth();
  const { t } = useLanguage(); // eslint-disable-line no-unused-vars
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    offerService.getOffers(token)
      .then(setOffers)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [token]);

  // Creation is moved to a dedicated page

  const openAddPage = () => {
    navigate('/offers/new');
  };

  // Edit functionality removed - can be added later if needed
  const openEditModal = (offer) => {
    // TODO: Navigate to edit page or implement edit modal
    alert('Edit functionality coming soon');
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;
    setLoading(true);
    await offerService.deleteOffer(token, offerId);
    offerService.getOffers(token)
      .then(setOffers)
      .catch(setError)
      .finally(() => setLoading(false));
  };

  const filteredOffers = offers.filter(o =>
    o.offer_title.toLowerCase().includes(search.toLowerCase())
  );

  const statusVariant = (status) => {
    if (status === 'active') return 'success';
    if (status === 'expired' || status === 'inactive') return 'secondary';
    return 'outline';
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Offers"
        actions={
          <Button onClick={openAddPage}>Add Offer</Button>
        }
      />

      <Input
        type="text"
        placeholder="Search offers..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="max-w-xs"
      />

      {loading ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <ShimmerBox width="w-64" height="h-10" />
            <ShimmerBox width="w-32" height="h-10" />
          </div>
          <ShimmerTable rows={8} columns={9} />
        </div>
      ) : error ? (
        <p className="text-destructive">{error.message || 'Error loading offers'}</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Discount %</TableHead>
                  <TableHead>Valid From</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Merchant Email</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOffers.map(offer => (
                  <TableRow key={offer.id}>
                    <TableCell>{offer.offer_title}</TableCell>
                    <TableCell className="max-w-xs truncate">{offer.offer_description}</TableCell>
                    <TableCell>{offer.discount_percentage}</TableCell>
                    <TableCell>{offer.valid_from?.slice(0, 10)}</TableCell>
                    <TableCell>{offer.valid_until?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(offer.status)}>{offer.status}</Badge>
                    </TableCell>
                    <TableCell>{offer.merchants?.business_name || '-'}</TableCell>
                    <TableCell>{offer.merchants?.email || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(offer)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(offer.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {/* Creation modal removed; use dedicated page */}
    </div>
  );
};

export default Offers;
